import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';

export const SURVEY_STATUS = ['draft', 'active', 'expired'] as const;
export type SurveyStatus = (typeof SURVEY_STATUS)[number];

export const surveyListItemSchema = z.object({
  id: z.coerce.number(),
  title: z.string(),
  description: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string(),
  status: z.enum(SURVEY_STATUS),
  questionCount: z.number(),
  respondentCount: z.number(),
  responseCount: z.number(),
  createdByName: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type SurveyListItem = z.infer<typeof surveyListItemSchema>;

export const surveyListTotalsSchema = z.object({
  active: z.number(),
  expired: z.number(),
  draft: z.number(),
  responses: z.number(),
});

export const paginatedSurveysSchema = z.object({
  data: z.array(surveyListItemSchema),
  meta: paginationMetaSchema.extend({ totals: surveyListTotalsSchema }),
});

export type PaginatedSurveys = z.infer<typeof paginatedSurveysSchema>;

export interface SurveyListParams {
  search?: string | undefined;
  status?: SurveyStatus | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export const SURVEY_QUESTION_TYPE = ['text', 'mcq', 'dropdown', 'checkbox', 'rating'] as const;
export type SurveyQuestionType = (typeof SURVEY_QUESTION_TYPE)[number];

export const QuestionType = {
  TEXT: 'text',
  MCQ: 'mcq',
  DROPDOWN: 'dropdown',
  CHECKBOX: 'checkbox',
  RATING: 'rating',
} as const satisfies Record<string, SurveyQuestionType>;

export const SURVEY_ANSWER_TYPE = ['text', 'number', 'decimal', 'percentage'] as const;
export type SurveyAnswerType = (typeof SURVEY_ANSWER_TYPE)[number];

export const AnswerType = {
  TEXT: 'text',
  NUMBER: 'number',
  DECIMAL: 'decimal',
  PERCENTAGE: 'percentage',
} as const satisfies Record<string, SurveyAnswerType>;

export const surveyQuestionOptionSchema = z.object({
  id: z.coerce.number(),
  text: z.string(),
  sortOrder: z.number(),
});

export type SurveyQuestionOption = z.infer<typeof surveyQuestionOptionSchema>;

export const surveyQuestionDetailSchema = z.object({
  id: z.coerce.number(),
  text: z.string(),
  type: z.enum(SURVEY_QUESTION_TYPE),
  sortOrder: z.number(),
  answerType: z.enum(SURVEY_ANSWER_TYPE).nullable(),
  isRequired: z.boolean().nullable(),
  minValue: z.number().nullable(),
  maxValue: z.number().nullable(),
  maxChars: z.number().nullable(),
  options: z.array(surveyQuestionOptionSchema).default([]),
});

export type SurveyQuestionDetail = z.infer<typeof surveyQuestionDetailSchema>;

export const surveyDetailSchema = z.object({
  id: z.coerce.number(),
  title: z.string(),
  description: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string(),
  status: z.enum(SURVEY_STATUS),
  respondentCount: z.number().optional(),
  responseCount: z.number().optional(),
  questions: z.array(surveyQuestionDetailSchema),
});

export type SurveyDetail = z.infer<typeof surveyDetailSchema>;

const CHOICE_QUESTION_TYPES: SurveyQuestionType[] = ['mcq', 'dropdown', 'checkbox'];

const surveyOptionFormShape = {
  id: z.number().int().positive().optional(),
  text: z.string().trim().min(1, 'Option text is required'),
};

const createSurveyOptionFormSchema = z.object(surveyOptionFormShape);

export type CreateSurveyOptionFormValues = z.infer<typeof createSurveyOptionFormSchema>;

const createSurveyQuestionFormSchema = z
  .object({
    id: z.number().int().positive().optional(),
    text: z.string().trim().min(1, 'Question text is required'),
    type: z.enum(SURVEY_QUESTION_TYPE),
    answerType: z.enum(SURVEY_ANSWER_TYPE).optional(),
    isRequired: z.boolean().optional(),
    minValue: z.number().optional(),
    maxValue: z.number().optional(),
    maxChars: z.number().int().positive().optional(),
    options: z.array(createSurveyOptionFormSchema).optional(),
  })
  .superRefine((question, ctx) => {
    if (question.type === 'text' && !question.answerType) {
      ctx.addIssue({
        code: 'custom',
        path: ['answerType'],
        message: 'Answer type is required for text questions',
      });
    }
    if (CHOICE_QUESTION_TYPES.includes(question.type)) {
      if (!question.options || question.options.length < 2) {
        ctx.addIssue({
          code: 'custom',
          path: ['options'],
          message: 'At least 2 options are required',
        });
      }
    }
    if (
      question.minValue !== undefined &&
      question.maxValue !== undefined &&
      question.minValue > question.maxValue
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['maxValue'],
        message: 'Max must be greater than or equal to Min',
      });
    }
  });

export type CreateSurveyQuestionFormValues = z.infer<typeof createSurveyQuestionFormSchema>;

export const createSurveyFormSchema = z
  .object({
    title: z.string().trim().min(1, 'Title is required'),
    description: z.string().trim().optional(),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    questions: z.array(createSurveyQuestionFormSchema).min(1, 'Add at least one question'),
  })
  .superRefine((survey, ctx) => {
    if (survey.startDate && survey.endDate && survey.startDate > survey.endDate) {
      ctx.addIssue({
        code: 'custom',
        path: ['endDate'],
        message: 'End date must be on or after Start date',
      });
    }
  });

export type CreateSurveyFormValues = z.infer<typeof createSurveyFormSchema>;

export interface CreateSurveyOptionPayload {
  text: string;
  sortOrder: number;
}

export interface CreateSurveyQuestionPayload {
  text: string;
  type: SurveyQuestionType;
  sortOrder: number;
  answerType?: SurveyAnswerType;
  isRequired?: boolean;
  minValue?: number;
  maxValue?: number;
  maxChars?: number;
  options?: CreateSurveyOptionPayload[];
}

export interface CreateSurveyPayload {
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
  questions: CreateSurveyQuestionPayload[];
}

export const createdSurveyRefSchema = z.object({
  id: z.coerce.number(),
});

export type CreatedSurveyRef = z.infer<typeof createdSurveyRefSchema>;

export type UpdateSurveyFormValues = CreateSurveyFormValues;

export interface UpdateSurveyOptionPayload {
  id?: number;
  text: string;
  sortOrder: number;
}

export interface UpdateSurveyQuestionPayload {
  id?: number;
  text: string;
  type: SurveyQuestionType;
  sortOrder: number;
  answerType?: SurveyAnswerType;
  isRequired?: boolean;
  minValue?: number;
  maxValue?: number;
  maxChars?: number;
  options?: UpdateSurveyOptionPayload[];
}

export interface UpdateSurveyPayload {
  title?: string;
  description?: string | null;
  startDate?: string;
  endDate?: string;
  questions?: UpdateSurveyQuestionPayload[];
}

const surveySubmissionPersonShape = {
  userId: z.coerce.number(),
  userName: z.string(),
  userHrId: z.string().nullable(),
  mobile: z.string().nullable(),
  designationName: z.string().nullable(),
  departmentName: z.string().nullable(),
  thanaName: z.string().nullable(),
  districtName: z.string().nullable(),
  divisionName: z.string().nullable(),
  reportingToName: z.string().nullable(),
};

export const surveySubmissionListItemSchema = z.object({
  id: z.coerce.number(),
  submissionDate: z.string(),
  submittedAt: z.string(),
  ...surveySubmissionPersonShape,
  answers: z.record(z.string(), z.string()),
});

export type SurveySubmissionListItem = z.infer<typeof surveySubmissionListItemSchema>;

export const paginatedSurveySubmissionsSchema = z.object({
  data: z.array(surveySubmissionListItemSchema),
  meta: paginationMetaSchema.extend({
    totals: z.object({ respondents: z.number() }),
  }),
});

export type PaginatedSurveySubmissions = z.infer<typeof paginatedSurveySubmissionsSchema>;

export const surveySubmissionAnswerSchema = z.object({
  questionId: z.coerce.number(),
  questionText: z.string(),
  questionType: z.enum(SURVEY_QUESTION_TYPE),
  sortOrder: z.number(),
  answerText: z.string().nullable(),
});

export type SurveySubmissionAnswer = z.infer<typeof surveySubmissionAnswerSchema>;

export const surveySubmissionDetailSchema = z.object({
  id: z.coerce.number(),
  surveyId: z.coerce.number(),
  surveyTitle: z.string(),
  submissionDate: z.string(),
  submittedAt: z.string(),
  ...surveySubmissionPersonShape,
  answers: z.array(surveySubmissionAnswerSchema),
});

export type SurveySubmissionDetail = z.infer<typeof surveySubmissionDetailSchema>;

export interface SurveySubmissionFilters {
  divisionId?: number | undefined;
  districtId?: number | undefined;
  thanaId?: number | undefined;
  departmentId?: number | undefined;
  reportingToId?: number | undefined;
  search?: string | undefined;
}

export interface SurveySubmissionListParams extends SurveySubmissionFilters {
  page?: number | undefined;
  limit?: number | undefined;
}

export interface SubmitSurveyAnswerPayload {
  questionId: number;
  answerValue?: string;
  optionIds?: number[];
}

export interface SubmitSurveyPayload {
  submissionDate?: string;
  answers: SubmitSurveyAnswerPayload[];
}

export const submittedSurveyRefSchema = z.object({
  id: z.coerce.number(),
});

export type SubmittedSurveyRef = z.infer<typeof submittedSurveyRefSchema>;

export type SurveyAnswerFieldValue = string | string[];

export type SurveyAnswerFormValues = Record<string, SurveyAnswerFieldValue>;
